import React, { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { EditableCell } from './EditableCell.component';
import { DeparturesChoiceComponent } from '../create/components/departuresChoice.component';
import { useDomainPath } from '../../auth/auth.context';
import { get } from '../../fetch';

/**
 * Interface pour les props du composant EditableDepartureMenu
 */
interface Props {
  activityId: string;
  value?: Dayjs;
  onChange: (date: Dayjs) => void;
  className?: string;
}

/**
 * Menu déroulant pour la sélection d'heure de départ
 * 
 * Ce composant :
 * - Récupère les créneaux de départ disponibles pour une activité et une date
 * - Affiche les créneaux disponibles via DeparturesChoiceComponent
 * - Utilise la date actuelle comme fallback si aucune date n'est fournie
 * 
 * Logique métier :
 * - Récupération des créneaux depuis l'API schedules
 * - Source backoffice pour les données internes
 * - Gestion des dates avec fallback sur la date courante
 * 
 * @param activityId ID de l'activité
 * @param value Date sélectionnée (optionnelle)
 * @param onChange Fonction de callback pour la sélection
 * @returns Composant React du menu de départ
 */
export const EditableDepartureMenu: React.FC<Props> = ({
  activityId,
  value,
  onChange,
}) => {
  const source: CRM.requestSource = 'backoffice';
  const schedulesPath = useDomainPath('/schedules');
  const [dayDepartures, setDayDepartures] = useState<
    CRM.ActivityOccurrence[] | undefined
  >();

  const fetchDayDepartures = useCallback(
    (date: Dayjs) => {
      const dateFormated = date.format('YYYYMMDD');
      get<CRM.ActivityOccurrence[]>(
        `${schedulesPath}/activities/${activityId}?day=${dateFormated}&source=${source}`,
      ).then(res => {
        setDayDepartures(res);
      });
    },
    [activityId, schedulesPath],
  );

  useEffect(() => fetchDayDepartures(value || dayjs()), [
    fetchDayDepartures,
    value,
  ]);

  return (
    <DeparturesChoiceComponent
      dayDepartures={dayDepartures}
      value={value}
      selectedDay={value || dayjs()}
      onChange={onChange}
    />
  );
};

/**
 * Interface pour les props du composant EditableDepartureCell
 */
interface Props {
  activityId: string;
  activityDate?: Dayjs;
  onChange: (date: Dayjs) => void;
  className?: string;
}

/**
 * Cellule éditable pour l'affichage et la sélection d'heure de départ
 * 
 * Ce composant :
 * - Affiche l'heure de départ formatée si disponible
 * - Ouvre un menu déroulant pour la sélection d'heure
 * - Ferme automatiquement le menu après sélection
 * 
 * Logique métier :
 * - Utilise EditableCell pour l'interface utilisateur
 * - Délègue la logique de sélection à EditableDepartureMenu
 * - Gestion de l'état d'ouverture/fermeture du menu
 * 
 * @param activityId ID de l'activité
 * @param activityDate Date de l'activité (optionnelle)
 * @param onChange Fonction de callback pour la sélection
 * @param className Classe CSS optionnelle
 * @returns Composant React de la cellule de départ éditable
 */
export const EditableDepartureCell: React.FC<Props> = ({
  className,
  activityDate,
  activityId,
  onChange,
}) => {
  const [isHourSelected, setIshourSelected] = useState<boolean>(false);

  return (
    <EditableCell
      content={
        <span>{activityDate && dayjs(activityDate).format('HH:mm')}</span>
      }
      className={className}
      closeCell={isHourSelected}
      onClosed={()=>{setIshourSelected(false)}}
    >
      <EditableDepartureMenu
        value={activityDate}
        activityId={activityId}
        onChange={(date)=>{onChange(date); setIshourSelected(true);}}
      />
    </EditableCell>
  );
};
